import { isRouteErrorResponse, Links, Meta, Outlet, Scripts, ScrollRestoration } from 'react-router';
import { ApolloProvider } from '@apollo/client';
import { Provider as JotaiProvider } from 'jotai';
import store from '@state/store';
import type { Route } from './+types/root';
import client from '@graphql/client';
import './app.css';

const brand = import.meta.env.VITE_BRAND || 'ticker';
const isRac = brand === 'rac';

const urlAssets = isRac ? import.meta.env.VITE_URL_ASSETS_RAC : import.meta.env.VITE_URL_ASSETS_TICKER;

export const links: Route.LinksFunction = () => [];

export function Layout({ children }: { children: React.ReactNode }) {
    return (
        <html dir={'ltr'} lang={'en-GB'}>
            <head>
                <meta charSet={'UTF-8'} />
                <meta httpEquiv={'X-UA-Compatible'} content={'IE=Edge,Chrome=1'} />
                <meta name={'viewport'} content={'width=device-width, initial-scale=1.0, maximum-scale=1.0'} />
                {isRac && <meta name={'msapplication-TileColor'} content={'#f95108'} />}
                {isRac && <meta name={'theme-color'} content={'#f95108'} />}
                <title>{isRac ? 'RAC' : 'Ticker'}</title>
                <Meta />

                {/* Preconnects */}
                <link href={'https://cdnjs.cloudflare.com'} rel={'preconnect'} />
                <link href={'https://js.intercomcdn.com'} rel={'preconnect'} />
                <link href={'https://api-iam.intercom.io'} rel={'preconnect'} />
                <link href={'https://widget.intercom.io'} rel={'preconnect'} />
                <link rel={'preconnect'} href={'https://dev.visualwebsiteoptimizer.com'} />

                {/*
                 * External brand CSS from the assets CDN. urlAssets is baked in at build time from
                 * VITE_URL_ASSETS_{TICKER,RAC}. These sheets are published inside a `web-assets`
                 * cascade layer, so all the local CSS below beats them without needing !important.
                 */}
                {urlAssets && <link href={`${urlAssets}/css/_vars.css`} rel={'stylesheet'} />}
                {urlAssets && <link href={`${urlAssets}/css/alert.css`} rel={'stylesheet'} />}
                {urlAssets && <link href={`${urlAssets}/css/button.css`} rel={'stylesheet'} />}
                {urlAssets && <link href={`${urlAssets}/css/form.css`} rel={'stylesheet'} />}
                {urlAssets && <link href={`${urlAssets}/css/general.css`} rel={'stylesheet'} />}
                {urlAssets && <link href={`${urlAssets}/css/layout.css`} rel={'stylesheet'} />}

                {/* Local CSS served from publicDir — unlayered, so it takes priority over the above */}
                <link href={'/css/quote-global.css'} rel={'stylesheet'} />

                <Links />

                {/* External scripts loaded before app bundle */}
                {urlAssets && <script src={`${urlAssets}/js/global.js`}></script>}
                {urlAssets && <script src={`${urlAssets}/js/analytics.js`}></script>}
                <script src={'https://js.stripe.com/v3/'}></script>
                {/* Runtime config — must load before app bundle */}
                <script type={'text/javascript'} src={'/config.js'}></script>
                <script
                    type={'text/javascript'}
                    src={'https://cdnjs.cloudflare.com/ajax/libs/iamdustan-smoothscroll/0.4.0/smoothscroll.min.js'}
                    defer
                />
                <script
                    type={'text/javascript'}
                    src={'//widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js'}
                    async
                />
                <script type={'text/javascript'} src={'/js/vwo.js'} id={'vwoCode'} />
            </head>
            <body>
                <noscript>
                    <iframe
                        src={'https://www.googletagmanager.com/ns.html?id=GTM-MLQXLMX'}
                        height={'0'}
                        width={'0'}
                        style={{ display: 'none', visibility: 'hidden' }}
                    />
                </noscript>
                {children}
                <ScrollRestoration />
                <Scripts />
            </body>
        </html>
    );
}

export default function App() {
    return (
        <JotaiProvider store={store}>
            <ApolloProvider client={client}>
                <Outlet />
            </ApolloProvider>
        </JotaiProvider>
    );
}

export function ErrorBoundary({ error }: Route.ErrorBoundaryProps) {
    let message = 'Oops!';
    let details = 'An unexpected error occurred.';
    let stack: string | undefined;

    if (isRouteErrorResponse(error)) {
        message = error.status === 404 ? '404' : 'Error';
        details = error.status === 404 ? 'The requested page could not be found.' : error.statusText || details;
    } else if (import.meta.env.DEV && error && error instanceof Error) {
        details = error.message;
        stack = error.stack;
    }

    return (
        <main style={{ padding: '16px' }}>
            <h1>{message}</h1>
            <p>{details}</p>
            {stack && (
                <pre style={{ overflow: 'auto' }}>
                    <code>{stack}</code>
                </pre>
            )}
        </main>
    );
}
